import type { SplitImageCalloutComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/** Split image/text callout renderer with panel variants and CTA button. */
export function SplitImageCalloutRenderer({ component }: { component: SplitImageCalloutComponent }) {
  const { imageSrc, imageAlt, title, text, ctaHref, ctaLabel, calloutVariant, panelBackground, buttonColor, imageFitTextHeight } =
    component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const resolvedCtaHref = resolveLinkHref(ctaHref);
  const variant = calloutVariant ?? "default";
  const legacyTheme = (component.content as { calloutTheme?: string }).calloutTheme;
  const panelBg = panelBackground ?? (legacyTheme === "light" ? "light" : "default");
  const isLightPanel = panelBg === "light";
  const isTransparentPanel = panelBg === "transparent";
  const btnColor = buttonColor ?? (legacyTheme === "light" ? "blue" : "default");
  const isBlueButton = btnColor === "blue";
  const fitHeight = imageFitTextHeight === true || String(imageFitTextHeight) === "true";

  const imageClasses = "rounded-t-xl ring-1 ring-black/10 md:rounded-none md:rounded-l-xl object-cover";

  // When fitHeight: enforce minimum height so the image is never 0 on mobile (inline style = no Tailwind/CSS dependency)
  const fitImageContainerStyle: React.CSSProperties | undefined = fitHeight
    ? { minHeight: "min(50vmin, 280px)" }
    : undefined;

  const grid = (
    <div className={`grid gap-0 overflow-hidden rounded-xl shadow-[var(--shadow-e2)] md:grid-cols-2 ${fitHeight ? "md:items-stretch" : ""}`}>
      {fitHeight ? (
        <div
          className={mergeClassNames(
            "apa-img-editorial relative min-h-0 overflow-hidden rounded-t-xl ring-1 ring-black/10 md:rounded-none md:rounded-l-xl",
            overrideProps.className
          )}
          style={mergeStyles(fitImageContainerStyle, overrideProps.style)}
        >
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover"
            style={objectPositionStyle}
            priority
          />
        </div>
      ) : (
        <div className="apa-img-editorial">
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className={mergeClassNames(`h-full w-full ${imageClasses}`, overrideProps.className)}
            style={mergeStyles(objectPositionStyle, overrideProps.style)}
            priority
          />
        </div>
      )}
      <div
        className={`flex flex-col items-start justify-center rounded-b-xl p-6 text-left md:rounded-none md:rounded-r-xl md:p-8 ${
          isTransparentPanel
            ? "bg-transparent"
            : isLightPanel
              ? "border-l-4 border-[#00B862] bg-[var(--color-surface-1)]"
              : "apa-surface-dark text-white ring-1 ring-[#00B862]/25"
        }`}
        style={isLightPanel || isTransparentPanel ? { color: "#121212" } : undefined}
      >
        <h3
          className={`text-lg font-bold tracking-tight xl:text-xl ${
            isLightPanel || isTransparentPanel ? "text-[#0A0A0A]" : "text-white"
          }`}
          style={{ fontFamily: "var(--font-aleo)" }}
        >
          {title}
        </h3>
        <p className={`mt-2 text-base leading-relaxed xl:text-lg ${isLightPanel || isTransparentPanel ? "text-[#121212]/80" : "text-white/75"}`}>{text}</p>
        {resolvedCtaHref && ctaLabel ? (
          <RuntimeLink
            href={resolvedCtaHref}
            className={`mt-5 inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition hover:-translate-y-0.5 xl:text-base ${
              isBlueButton
                ? "bg-[#00B862] text-white hover:shadow-[var(--shadow-glow)]"
                : "bg-[#FFC105] text-[#0A0A0A] hover:shadow-[var(--shadow-e2)]"
            }`}
          >
            {ctaLabel}
          </RuntimeLink>
        ) : null}
      </div>
    </div>
  );

  if (variant === "fullWidth") {
    const sectionBg = "var(--color-surface-1)";
    return (
      <section className="mx-[calc(50%-50vw)] py-10" style={{ backgroundColor: sectionBg }}>
        <div className="mx-auto max-w-7xl px-6 md:px-8">{grid}</div>
      </section>
    );
  }

  return <section>{grid}</section>;
}


